import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore, ZOOM } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { TOWN_LOCATIONS } from '../../rooms';
import { serverNow } from '../../services/worldSocket';
import { audioEngine } from '../../services/audioEngine';
import { takeAudio, releaseAudio, onAudio, keepSounding } from '../../services/audioPool';
import { useNarrow } from '../SceneChat';
import {
  useHeliTour, usePilots, pilotFor, FLIGHTS, SEATS, NARRATION_V, flightById, padNumber, planFor, trackFor, HELIPORT, heliNow, flightT,
} from '../../city/heliTour';
import { boardPad, leavePad, pickFlight, takeOff } from '../../city/heliActions';
import HeliTalk from './HeliTalk';

// THE HELIPORT'S BUTTONS AND THE FLIGHT'S WORDS (DOM; city/heliTour).
//
//   AT A PAD (Heliport sets `near`): what that helicopter is doing — free, x of
//     5 aboard and who, or in the air and back in how long — and Board.
//   ABOARD, ON THE PAD: who is aboard; the first aboard picks one of the three
//     flights and takes off (the rest see their pick); anyone can get out.
//   LIFTING OFF: the countdown the server stamped.
//   IN THE AIR: the flight and how far through it, the pilot's words (or the
//     place below), and the camera: behind it, or a window seat.
//
// THE PILOT'S WORDS run on the flight's clock (heliNow − departAt), slot by
// slot (city/heliRoute speakingPlan): a recorded line (data/heli_flights.json,
// the pilot's own, in their voice) plays when its slot comes up; a line still
// talking when the next is due finishes first unless the next would be past its
// moment. Nothing recorded yet: the slot shows the place's name ("Below: …"),
// and the what's-on slot shows what is on (the city's own sets and stories, as
// the screen's words — never put in the pilot's mouth).
const GOLD = '#f2b632';
const NAVY = 'rgba(14,20,32,0.94)';
const BASE = import.meta.env.BASE_URL || '/';

function placeName(slug, snap) {
  if (!slug) return '';
  const loc = TOWN_LOCATIONS.find((l) => l.key === slug || l.interiorId === slug);
  if (loc?.name) return loc.name;
  const pl = (snap?.places || []).find((p) => p.slug === slug);
  if (pl?.name) return pl.name;
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The what's-on line from the city's own data, or null (nothing on). */
function whatsOn(snap) {
  const now = serverNow();
  const show = (snap?.shows || []).find((s) => s && s.t1 > now);
  if (show) {
    const at = placeName(show.place, snap);
    const mins = Math.round((show.t0 - now) / 60000);
    return mins > 1 ? `Coming up: ${show.name} at ${at}, in ${mins} minutes` : `Live now: ${show.name} at ${at}`;
  }
  const story = (snap?.stories || []).find((s) => s?.title);
  if (story) return `Today in the city: ${story.name ? `${story.name}, ` : ''}“${story.title}”`;
  return null;
}

const clock = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

/** Plays the pilot's lines and sets the caption while you are in the air. */
function useNarration(padId, p, aboard) {
  const el = useRef(null);
  const rec = useRef(null);   // the line playing now, for keepSounding
  const played = useRef(new Set());
  const runKey = p?.state === 'flying' ? `${padId}:${p.flight}:${p.departAt}` : null;
  useEffect(() => {
    played.current = new Set();
    if (!runKey || !aboard) return undefined;
    const plan = planFor(p.flight, padId, pilotFor(padId)?.avatarId);   // the pad's pilot's own recordings
    const f = flightById(p.flight);
    let ducked = false;
    const stop = () => {
      if (el.current) { try { el.current.pause(); } catch { /* gone */ } releaseAudio(el.current); el.current = null; }
      rec.current = null;
      if (ducked) { audioEngine.duck(false); ducked = false; }
      useHeliTour.setState((st) => (st.tour?.playing ? { tour: { ...st.tour, playing: false } } : {}));
    };
    const iv = setInterval(() => {
      // THE ONE FLIGHT CLOCK (city/heliTour flightT): a hover while she talks holds the tour where it is.
      const t = flightT(useHeliTour.getState().pads[padId] || p, heliNow());
      // SHE IS ANSWERING SOMEONE (HeliTalk): the tour line under her stops, cut where it was
      // (Khari: "cut in naturally, and let her flow"), and nothing new starts until she is done.
      if (useHeliTour.getState().pilotTalking) { if (el.current) stop(); return; }
      // Her line playing now, its start asked again if it was refused (services/audioPool).
      if (rec.current && el.current === rec.current.el) keepSounding(rec.current, { label: 'heli' });
      const slot = plan.find((s, i) => !played.current.has(i) && t >= s.t && t < s.until);
      if (!slot) return;
      const i = plan.indexOf(slot);
      const busy = el.current && !el.current.paused && !el.current.ended;
      if (busy && t < slot.until - 2) return;   // the last line finishes first, unless this one's moment is going
      played.current.add(i);
      const snap = useCityStore.getState().snap;
      const line = slot.line;
      if (line?.audio) {
        stop();
        const a = takeAudio(`${BASE}${line.audio.replace(/^\//, '')}?v=${NARRATION_V}`);
        el.current = a;
        audioEngine.duck(true); ducked = true;
        onAudio(a, 'ended', () => { if (el.current === a) stop(); });
        rec.current = { el: a };
        keepSounding(rec.current, { label: 'heli' });
        // What is below and whether she is mid-line: said with anything a passenger says to her (HeliTalk).
        useHeliTour.setState({ caption: { text: line.text, kind: slot.kind, pilot: true }, tour: { playing: true, below: slot.kind === 'place' ? placeName(slot.cue, snap) : null } });
        return;
      }
      // Nothing recorded: the screen names what is below (or what is on).
      const text = slot.kind === 'place' ? `Below: ${placeName(slot.cue, snap)}`
        : slot.kind === 'event' ? whatsOn(snap)
          : slot.kind === 'intro' ? `${f?.title || 'Heli tour'} · lifting off`
            : 'Coming in to land';
      useHeliTour.setState({ caption: text ? { text, kind: slot.kind, pilot: false } : null, tour: { playing: false, below: slot.kind === 'place' ? placeName(slot.cue, snap) : null } });
    }, 200);
    return () => { clearInterval(iv); stop(); };
  }, [runKey, aboard]); // eslint-disable-line react-hooks/exhaustive-deps
}

const btn = (primary = false, on = true) => ({
  cursor: on ? 'pointer' : 'default', opacity: on ? 1 : 0.45, borderRadius: 999, padding: '9px 16px',
  border: `1px solid ${GOLD}${primary ? '' : '88'}`, background: primary ? GOLD : 'transparent', color: primary ? '#141821' : GOLD,
  font: '700 13px system-ui', whiteSpace: 'nowrap',
});

export default function HeliPanel() {
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const near = useHeliTour((s) => s.near);
  const mine = useHeliTour((s) => s.mine);
  const pads = useHeliTour((s) => s.pads);
  const myId = useHeliTour((s) => s.myId);
  const offline = useHeliTour((s) => s.offline);
  const view = useHeliTour((s) => s.view);
  const caption = useHeliTour((s) => s.caption);
  const pilotName = usePilots((s) => pilotFor(mine?.pad || near, s)?.name || null);
  const narrow = useNarrow();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [, tick] = useState(0);
  const padId = mine?.pad || near;
  const p = padId ? pads[padId] : null;
  const aboard = !!mine && !!p && p.state !== 'idle';
  useNarration(padId, p, aboard);
  useEffect(() => {
    if (!padId) return undefined;
    const iv = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(iv);
  }, [padId]);
  useEffect(() => { setNote(null); }, [padId]);

  if (interior || zoom !== ZOOM.STREET || !padId) return null;
  const state = p?.state || 'idle';
  const seats = p?.seats || [];
  const flight = flightById(p?.flight) || FLIGHTS[0];
  const host = offline || (p?.host && p.host === myId);
  const n = padNumber(padId);
  const act = async (fn) => {
    if (busy) return;
    setBusy(true); setNote(null);
    try {
      const r = await fn();
      if (r && r.ok === false && !r.offline) {
        setNote(r.reason === 'full' ? 'That helicopter is full — try another pad.' : r.reason === 'flying' ? "It's in the air — it'll be back soon." : r.reason === 'not_here' ? 'Step onto the street first.' : 'That did not go through — try again.');
      }
    } finally { setBusy(false); }
  };
  const wrap = narrow
    ? { position: 'fixed', left: 10, right: 10, bottom: 'calc(max(12px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px))' }
    : { position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 'calc(max(18px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px) + 56px)', width: 440 };
  const card = { ...wrap, zIndex: 74, pointerEvents: 'auto', background: NAVY, border: `1px solid ${GOLD}55`, borderRadius: 16, padding: '12px 14px', color: '#eef1f5', fontFamily: 'system-ui, sans-serif', boxShadow: '0 18px 50px rgba(0,0,0,0.5)' };
  const head = (
    <div style={{ font: '700 10.5px system-ui', letterSpacing: 2.4, color: GOLD, textTransform: 'uppercase' }}>{HELIPORT.name} · Pad {n}{pilotName ? ` · Pilot ${pilotName}` : ''}</div>
  );
  const who = seats.map((s) => (s.id === myId ? 'You' : s.handle || 'Guest'));
  // AN ARTIST GOING UP (API city/HeliRideService, 2026-11-05; Khari: "Users can ride along if they like"):
  // they boarded first, picked the flight, and lift off at `leaveAt`: hop in before then.
  const artistHost = seats.find((s) => s.avatarId && s.id === p?.host) || null;
  const leavesIn = state === 'boarding' && p?.leaveAt ? clock(Math.max(0, p.leaveAt - heliNow())) : null;

  // ── IN THE AIR (or lifting off) ────────────────────────────────────────────
  if (aboard && state === 'flying') {
    const tr = trackFor(p.flight, padId);
    const left = p.departAt - heliNow();
    const t = Math.max(0, flightT(p, heliNow()));
    const pct = tr ? Math.min(100, (t / tr.seconds) * 100) : 0;
    // Her words sit above the talk bar (HeliTalk), which takes the bottom.
    const captionWrap = { ...wrap, bottom: `calc(${wrap.bottom} + 104px)` };
    return (
      <>
        <div style={{ position: 'fixed', top: 'max(14px, env(safe-area-inset-top))', left: '50%', transform: 'translateX(-50%)', zIndex: 74, pointerEvents: 'auto', background: NAVY, border: `1px solid ${GOLD}55`, borderRadius: 999, padding: '7px 14px', color: '#eef1f5', display: 'flex', alignItems: 'center', gap: 12, font: '600 13px system-ui' }}>
          <span style={{ color: GOLD }}>{flight.title}</span>
          <span style={{ width: 120, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${pct}%`, background: GOLD }} /></span>
          <span style={{ display: 'flex', gap: 4 }}>
            {[['chase', 'Behind'], ['cabin', 'Window']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => useHeliTour.setState({ view: k })} style={{ ...btn(view === k), padding: '4px 10px', font: '700 11.5px system-ui' }}>{label}</button>
            ))}
          </span>
        </div>
        {left > 0 && (
          <div style={{ position: 'fixed', left: '50%', top: '38%', transform: 'translate(-50%,-50%)', zIndex: 74, pointerEvents: 'none', color: GOLD, font: '800 54px system-ui', textShadow: '0 4px 20px rgba(0,0,0,0.6)' }}>
            {Math.ceil(left / 1000)}
          </div>
        )}
        {left <= 0 && <HeliTalk padId={padId} p={p} wrap={wrap} />}
        {caption?.text && (
          <div style={{ ...captionWrap, zIndex: 74, pointerEvents: 'none', textAlign: 'center' }}>
            <div style={{ display: 'inline-block', maxWidth: '100%', background: 'rgba(8,12,20,0.78)', borderRadius: 12, padding: '10px 14px', color: '#f4f6f8', font: `${caption.pilot ? '500 15px' : '600 14px'} system-ui`, lineHeight: 1.45, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
              {caption.text}
            </div>
          </div>
        )}
      </>
    );
  }

  // ── ABOARD, ON THE PAD ─────────────────────────────────────────────────────
  if (aboard && state === 'boarding') {
    return (
      <div style={card} role="dialog" aria-label="Heli tour">
        {head}
        <div style={{ font: '700 16px system-ui', margin: '4px 0 8px' }}>You're aboard · {seats.length} of {SEATS}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {who.map((h, i) => <span key={i} style={{ fontSize: 12, padding: '3px 9px', borderRadius: 999, background: 'rgba(242,182,50,0.14)', border: `1px solid ${GOLD}44` }}>{h}</span>)}
          {Array.from({ length: Math.max(0, SEATS - seats.length) }, (_, i) => <span key={`e${i}`} style={{ fontSize: 12, padding: '3px 9px', borderRadius: 999, border: '1px dashed rgba(255,255,255,0.2)', opacity: 0.55 }}>free seat</span>)}
        </div>
        {host ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {FLIGHTS.map((f) => {
              const on = f.id === flight.id;
              return (
                <button key={f.id} type="button" onClick={() => act(() => pickFlight(f.id))} style={{ textAlign: 'left', cursor: 'pointer', borderRadius: 12, padding: '8px 11px', border: `1px solid ${on ? GOLD : 'rgba(242,182,50,0.28)'}`, background: on ? 'rgba(242,182,50,0.16)' : 'rgba(255,255,255,0.03)', color: '#eef1f5' }}>
                  <span style={{ display: 'block', font: '700 13.5px system-ui' }}>{f.title} <span style={{ opacity: 0.6, fontWeight: 500 }}>· {f.seconds}s</span></span>
                  <span style={{ display: 'block', fontSize: 12, opacity: 0.7, marginTop: 2 }}>{f.blurb}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 10 }}>{flight.title} — {who[0] || 'the first aboard'} takes off {leavesIn ? `in ${leavesIn}` : "when everyone's in"}.{artistHost ? ` You're riding along with ${artistHost.handle}; ${pilotName || 'the pilot'} is the one on the headset.` : ''}</div>
        )}
        {offline && <div style={{ fontSize: 11.5, opacity: 0.6, marginBottom: 8 }}>Flying on your own — the city's server isn't taking heli tours right now, so friends can't join this one.</div>}
        {note && <div style={{ fontSize: 12, color: '#ffb4a8', marginBottom: 8 }}>{note}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={btn(false, !busy)} disabled={busy} onClick={() => act(leavePad)}>Get out</button>
          {host && <button type="button" style={btn(true, !busy)} disabled={busy} onClick={() => act(takeOff)}>Take off</button>}
        </div>
      </div>
    );
  }

  // ── AT A PAD ───────────────────────────────────────────────────────────────
  const full = seats.length >= SEATS;
  const back = state === 'flying' && p?.endsAt ? clock(p.endsAt - heliNow()) : null;
  return (
    <div style={card} role="dialog" aria-label="Heli tour">
      {head}
      <div style={{ font: '700 16px system-ui', margin: '4px 0 4px' }}>
        {state === 'flying' ? `In the air${back ? ` · back in ${back}` : ''}` : artistHost ? `${artistHost.handle} is going up · ${flight.title}` : state === 'boarding' ? `${seats.length} of ${SEATS} aboard` : 'A 90-second flight over the city'}
      </div>
      <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 10 }}>
        {artistHost && state === 'boarding' ? `Lifting off${leavesIn ? ` in ${leavesIn}` : ' soon'}. Hop in to ride along — ${seats.length} of ${SEATS} aboard.` : state === 'boarding' ? `With ${who.join(', ')}. Hop in — whoever boarded first picks the flight.` : state === 'flying' ? 'Wait here, or take another pad.' : 'Five seats. Bring friends: they board this pad too, and you all fly together.'}
      </div>
      {note && <div style={{ fontSize: 12, color: '#ffb4a8', marginBottom: 8 }}>{note}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" style={btn(true, state !== 'flying' && !full && !busy)} disabled={state === 'flying' || full || busy} onClick={() => act(() => boardPad(padId))}>
          {full ? 'Full' : 'Board'}
        </button>
      </div>
    </div>
  );
}
